import useSidebarStack from '@/common/sidebar/useSidebarStack.ts';
import {
  ActionIcon,
  Alert,
  Button,
  Group,
  Paper,
  ScrollArea,
  Skeleton,
  Stack,
  Text,
  Title,
} from '@mantine/core';
import { useSupportCasesControllerGetSupportCaseMessages } from '@/generated/api/support-cases.ts';
import SupportCaseHeader from '@/features/support-cases/components/SupportCaseHeader.tsx';
import { SupportCaseDescription } from '@/features/support-cases/components/SupportCaseDescription.tsx';
import { MessagesList } from '@/common/components/MessagesList.tsx';
import { ArrowLeftIcon } from '@phosphor-icons/react';
import CollapsedSidebar from '@/common/sidebar/CollapsedSidebar.tsx';
import type { ExtraSidebarParams } from '@/common/sidebar/sidebar-registry.tsx';

export default function SupportCaseDetailsSidebar({
  supportCaseId,
  collapsed,
  depth,
  hovered,
}: {
  supportCaseId: string;
} & ExtraSidebarParams) {
  const sidebarStack = useSidebarStack();

  const supportCase =
    useSupportCasesControllerGetSupportCaseMessages(supportCaseId);

  if (collapsed) {
    return (
      <CollapsedSidebar
        title={`Support Case #${supportCase.data?.caseId ?? 'Unknown'}`}
        depth={depth}
        hovered={hovered}
      />
    );
  }

  return (
    <Stack
      gap="md"
      h="100%"
      style={{
        overflow: 'hidden',
      }}
    >
      <Group>
        <ActionIcon
          size="sm"
          variant="transparent"
          onClick={() => sidebarStack.popItem()}
        >
          <ArrowLeftIcon size={32} />
        </ActionIcon>

        <Title order={4}>Support Case</Title>
      </Group>

      {supportCase.isLoading ? (
        <ScrollArea
          style={{
            flex: 1,
            minHeight: 0,
          }}
        >
          <Stack gap="sm">
            <Skeleton height={32} radius="md" />
            <Skeleton height={118} radius="md" />
            <Skeleton height={176} radius="md" />
          </Stack>
        </ScrollArea>
      ) : supportCase.isError ? (
        <ScrollArea
          style={{
            flex: 1,
            minHeight: 0,
          }}
        >
          <Alert color="red" title="Support case failed to load">
            <Stack gap="sm">
              <Text size="sm">
                Check your connection and retry. If this continues, return to
                the support case list and select the case again.
              </Text>
              <Button
                size="xs"
                variant="light"
                onClick={() => void supportCase.refetch()}
              >
                Retry
              </Button>
            </Stack>
          </Alert>
        </ScrollArea>
      ) : (
        <>
          <ScrollArea style={{ height: '100%' }}>
            <Stack gap="sm">
              <SupportCaseHeader supportCase={supportCase.data!} />
              <Title order={5}>Description</Title>
              <Paper withBorder radius="md" p="sm">
                <SupportCaseDescription
                  description={supportCase.data!.description}
                />
              </Paper>
              <Stack gap={0}>
                <Title order={5}>Messages</Title>
                <Text size="sm" c="dimmed">
                  Most recent message is at the top
                </Text>
              </Stack>
              {supportCase.data!.messages.length ? (
                <MessagesList
                  messages={supportCase.data!.messages.map((message) => ({
                    id: message.id,
                    message: message.text,
                    createdAt: message.createdAt,
                    fromUser: message.senderName,
                    color: message.senderEmail.includes(
                      '@worldlinkintegration.com',
                    )
                      ? 'gray'
                      : 'orange',
                  }))}
                />
              ) : (
                <Alert color="gray" title="No messages yet">
                  This support case does not have any messages.
                </Alert>
              )}
            </Stack>
          </ScrollArea>
        </>
      )}
    </Stack>
  );
}
